import { useEffect, useRef, useState, type RefObject } from "react";
import { observer } from "mobx-react-lite";
import { useParams, useSearchParams } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Clock,
  FolderOpen,
  History,
  List,
  Loader2,
  Link as LinkIcon,
  MoreVertical,
} from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useScrollReset } from "@/hooks/useScrollReset";
import { BuildingDocuments as BuildingDocumentsComponent } from "@/domains/documents/components/BuildingDocuments";
import { AddedDateFilter } from "@/domains/documents/components/filters/AddedDateFilter";
import { useMarkProjectActivitySeen } from "@/hooks/projects/useProjectActivity";
import { BuildingDocumentFolders } from "@/domains/documents/components/BuildingDocumentFolders";
import { ProjectScopedFolderView } from "@/domains/projects/components/ProjectScopedFolderView";
import { PageTitle } from "@/components/ui/page-title";
import { PageSubtitle } from "@/components/ui/page-subtitle";
import { UnderlineTabs } from "@/components/ui/underline-tabs";
import type { UnderlineTabOption } from "@/components/ui/underline-tabs";
import { PageHeaderActionsProvider } from "@/components/layout/PageHeaderActions";
import {
  DocumentFiltersProvider,
  useDocumentFilters,
} from "@/domains/documents/context/DocumentFiltersContext";
import { DocumentFilterControl } from "@/domains/documents/components/filters";
import { DocumentSearchInput } from "@/domains/documents/components/molecules/DocumentSearchInput";
import { useProjects } from "@/domains/documents/hooks/useProjects";
import { useDocumentsRealtimeFlush } from "@/domains/documents/hooks/useDocumentsRealtimeBridge";
import { useDocumentStore } from "@/hooks/documents/useDocumentStore";
import { useBuildingDetail } from "@/hooks/buildings/useBuildingDetail";
import { StorageCriticalBanner } from "@/domains/documents/components/StorageCriticalBanner";
import { migrateLegacyScopeParams, useBuildingScope } from "@/hooks/buildings/useBuildingScope";
import { useBuildingViewMode } from "@/hooks/buildings/useBuildingViewMode";
import type { BuildingViewMode } from "@/hooks/buildings/useBuildingViewMode";
import { usePermissions } from "@/hooks/usePermissions";
import { PERMISSIONS } from "@/constants/permissions";
import {
  DOC_VIEW_MODE_STORAGE_KEY,
  loadStoredDocViewMode,
  resolveDocViewMode,
} from "@/domains/documents/utils/docViewMode";
import type { DocViewMode } from "@/domains/documents/utils/docViewMode";
import { ManageSharesDialog } from "@/domains/documents/components/organisms/ManageSharesDialog";
import { DocumentHistoryDialog } from "@/domains/documents/components/DocumentHistoryDialog";
import { DocumentCommentsPanelProvider } from "@/domains/documents/components/organisms/DocumentCommentsPanelProvider";
import { useDocumentCommentsPanel } from "@/domains/documents/comments/documentCommentsPanelContext";
import { useAuth } from "@/hooks/useAuth";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import type { Document } from "@/types/documents";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

/**
 * Hoisted so the reference is stable. `useTagGroups` neutralises this input
 * once availableFilters has loaded, but until then it memoises on it — and a
 * fresh `[]` per render would rebuild every tag group on the way through.
 */
function StorageLargestFilesSort() {
  const [params] = useSearchParams();
  const { setSortKey, setSortOrder } = useDocumentFilters();
  const largest = params.get("storage") === "largest";
  useEffect(() => {
    if (largest) {
      setSortKey("size");
      setSortOrder("desc");
    }
  }, [largest, setSortKey, setSortOrder]);
  return null;
}

const NO_FALLBACK_DOCUMENTS: Document[] = [];

/**
 * Page-level tools row: the cascading Filters control sitting directly next to
 * the document search, both pushed to the far right so they line up under the
 * title row's Upload + overflow buttons.
 *
 * Both controls belong to the PAGE, not to whichever tab is mounted — they stay
 * put across Folders / Timeline / List, and each view reads the shared
 * DocumentFiltersContext they write to. Rendered inside the provider, hence a
 * separate component.
 */
const DocumentsToolsRow = observer(function DocumentsToolsRow({
  buildingId,
  selectedProjectId,
  searchPlaceholder,
  canViewHistory,
  onOpenHistory,
  historyTriggerRef,
}: {
  buildingId: string;
  selectedProjectId: string | null;
  searchPlaceholder?: string;
  canViewHistory: boolean;
  onOpenHistory: () => void;
  historyTriggerRef: RefObject<HTMLButtonElement>;
}) {
  const { t } = useLanguage();
  const { filters, setActiveTagFilters } = useDocumentFilters();
  const { projects } = useProjects(buildingId);
  const documentStore = useDocumentStore();
  const { userProfile } = useAuth();
  const { currentOrganization, isReady } = useOrganizationContext();

  // The INITIAL load of the filter values lives on the page component below,
  // not here: this row unmounts in the trash views, while the Folders tab holds
  // its rows behind a spinner until those values arrive. The realtime refresh
  // below stays with the row, since it refreshes what the row itself renders.
  //
  // Every one of these is derived from the same `documents` rows the store
  // already receives events for, but none of them was ever recomputed after
  // the initial load — so the Filters popover offered pre-classification
  // categories, and the document count sat short, right next to a processing
  // badge that *is* live off realtime. One screen contradicting itself.
  //
  // The counts and tags belong to the mounted view, which reloads them on a
  // scope change; they are refreshed here anyway because they come from these
  // same rows and the view has no event of its own to react to.
  //
  // Riding the bridge `BuildingLayout` already mounts rather than opening a
  // second channel on the same rows: both want every `documents` change for
  // this building, and a bulk upload would otherwise deliver each row twice
  // over per open tab. DocumentStore's own subscription is not reusable for
  // either — it holds a single channel slot claimed by whichever document
  // surface mounted last, and it is not subscribed at all on the routes the
  // bridge's other consumers render on.
  useDocumentsRealtimeFlush(
    () => {
      const projectScope = selectedProjectId ?? undefined;
      documentStore.loadAvailableFilters(buildingId, projectScope).catch(() => {
        // Logged by the store; the popover keeps the values it already has.
      });
      // Both flows rethrow after logging, and nothing here awaits them — an
      // uncaught one becomes an unhandled rejection on every failed refresh.
      //
      // Scope races are the store's to settle, not this callback's: a refresh
      // fired here for the previous project can still be in flight when the
      // switch lands, so loadDocumentCount and loadTags each keep a request seq
      // (like loadAvailableFilters) and only the newest one writes.
      documentStore.loadDocumentCount(buildingId, projectScope).catch(() => {
        // Logged by the store; the badge keeps the count it already has.
      });
      documentStore.loadTags(buildingId, projectScope).catch(() => {
        // Logged by the store; the tag chips keep what they already have.
      });
    },
    Boolean(buildingId) && isReady && Boolean(currentOrganization) && Boolean(userProfile)
  );

  // If that load failed (transient network error), retry when the user opens
  // the Filters popover instead of showing four empty dimensions all session.
  const handleFilterOpen = () => {
    if (documentStore.availableFilters || documentStore.isLoadingFilters) return;
    documentStore.loadAvailableFilters(buildingId, selectedProjectId ?? undefined).catch(() => {
      // Logged by the store; the popover just stays without filter values.
    });
  };

  return (
    <div className="flex w-full min-w-0 flex-wrap items-center justify-end gap-2">
      <DocumentFilterControl
        // `documents` is only the fallback source of filter values; the store's
        // availableFilters (loaded by the page) covers the whole scope, so no view's
        // current page of rows needs to be threaded in here.
        documents={NO_FALLBACK_DOCUMENTS}
        projects={projects}
        activeFilters={filters.activeTagFilters}
        onFilterChange={setActiveTagFilters}
        availableFilters={documentStore.availableFilters}
        isLoading={documentStore.isLoadingFilters}
        hasError={documentStore.filtersLoadFailed}
        onOpen={handleFilterOpen}
        lockedProjectId={selectedProjectId ? Number(selectedProjectId) : null}
      />
      <AddedDateFilter />
      <DocumentSearchInput className="min-w-32 flex-[0_1_14rem]" placeholder={searchPlaceholder} />
      {canViewHistory && (
        <Button
          ref={historyTriggerRef}
          variant="ghost"
          size="controlIcon"
          className="text-muted-foreground hover:text-foreground"
          onClick={onOpenHistory}
          aria-label={t.documents.log.history.actionLabel}
          {...pageInfoTarget("documents.history")}
          title={t.documents.log.history.actionLabel}
        >
          <History />
        </Button>
      )}
    </div>
  );
});

/**
 * `?comments=<threadId>` (digest emails, the issue panel's Origin line): open
 * the thread's document in the viewer on that thread, then drop the param so a
 * closed viewer does not re-open on the next render. Handled once per value.
 */
const CommentsDeepLink = observer(function CommentsDeepLink() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isReady } = useOrganizationContext();
  const panel = useDocumentCommentsPanel();
  const handledRef = useRef<string | null>(null);
  const param = searchParams.get("comments");
  useEffect(() => {
    if (!param || !isReady || !panel) return;
    if (handledRef.current === param) return;
    handledRef.current = param;
    const next = new URLSearchParams(searchParams);
    next.delete("comments");
    setSearchParams(next, { replace: true });
    const threadId = Number(param);
    if (Number.isFinite(threadId) && threadId > 0) void panel.openThread(threadId);
  }, [param, isReady, panel, searchParams, setSearchParams]);
  return null;
});

const BuildingDocuments = observer(function BuildingDocuments() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useLanguage();
  const perms = usePermissions();
  const buildingId = id ?? "";
  const buildingIdNum = Number(buildingId);
  const documentStore = useDocumentStore();
  const { currentOrganization, isReady } = useOrganizationContext();
  const { userProfile } = useAuth();
  // Global building scope drives the project scope. In Building view it
  // resolves to null (whole building — the Folders tab offers a project to
  // open); in Projects view the sidebar picker sets it and the Folders tab
  // mounts scoped to it.
  const {
    selectedProjectId,
    setProject,
    projectOptions,
    optionsLoaded,
    optionsError,
    reloadOptions,
  } = useBuildingScope(buildingId);
  const selectedProjectIdStr = selectedProjectId != null ? String(selectedProjectId) : null;
  const { viewMode: sidebarMode } = useBuildingViewMode(buildingId);
  const isTrashView = searchParams.get("view") === "trash";
  useScrollReset();

  // Manage shares dialog state
  const [isManageSharesDialogOpen, setIsManageSharesDialogOpen] = useState(false);
  const [isDocumentHistoryOpen, setIsDocumentHistoryOpen] = useState(false);
  const historyTriggerRef = useRef<HTMLButtonElement>(null);

  // The Folders tab owns its per-project trash internally; it reports the state
  // up so the page-level tools row can step aside (the trash has its own).
  const [isFolderTrashOpen, setIsFolderTrashOpen] = useState(false);

  // Title-row slot the active tab renders its Upload button + overflow menu
  // into (see PageHeaderActions). Held in state so the portal re-renders once
  // the node is attached.
  const [headerActionsNode, setHeaderActionsNode] = useState<HTMLDivElement | null>(null);

  // Tab preference (lifted so it survives trash-view round-trips). The raw
  // stored value may name a tab the current sidebar mode doesn't offer —
  // resolveDocViewMode clamps at render time.
  const [rawViewMode, setRawViewMode] = useState<DocViewMode | null>(loadStoredDocViewMode);

  // Which tabs the current sidebar mode + role offer. Folders needs the
  // /folders page's old grant; Timeline/List keep the documents grant —
  // non-sub users hold both implicitly (mirrors the sidebar's any-of gate).
  const canUseFolders =
    !perms.isSubcontractorOnly ||
    perms.hasPermission(PERMISSIONS.BUILDING_VIEW_PROJECTS, { buildingId: buildingIdNum });
  const canUseTimelineList =
    !perms.isSubcontractorOnly ||
    perms.hasPermission(PERMISSIONS.VIEW_DOCUMENTS_LIST_TIMELINE, { buildingId: buildingIdNum });
  // History is the same class of data ProjectDocumentsLog gates on
  // PROJECT_VIEW_LOG — who deleted or restored which document, and when — so
  // both scopes ask for that grant. Building-wide there is no project to check
  // it against, and `event_logs` RLS is org-wide (nothing server-side re-scopes
  // it), so the merged log spans every project in the building: the grant has to
  // be held outright, not on one project. Timeline/List access is still
  // required — history is those tabs' tool.
  const canViewDocumentHistory =
    selectedProjectId !== null
      ? perms.canViewLogOnProject(selectedProjectId)
      : !perms.isSubcontractorOnly &&
        canUseTimelineList &&
        perms.hasPermission(PERMISSIONS.PROJECT_VIEW_LOG);
  const availableModes: DocViewMode[] = [
    ...(canUseFolders ? (["folders"] as const) : []),
    ...(canUseTimelineList ? (["timeline", "list"] as const) : []),
  ];

  const viewMode = resolveDocViewMode(
    searchParams.get("storage") === "largest" ? "list" : rawViewMode,
    availableModes
  );

  const handleViewModeChange = (newMode: DocViewMode) => {
    setRawViewMode(newMode);
    localStorage.setItem(DOC_VIEW_MODE_STORAGE_KEY, newMode);
    // Each tab's URL state belongs to that tab, so leaving one drops it:
    //  - `?view=trash` is Timeline/List's trash; the Folders tab has its own,
    //    so tabbing back must not silently re-enter the trash view.
    //  - `?folderId=` is the Folders tab's navigation state. Left behind it
    //    would keep advertising a folder location no mounted view shows, and
    //    the effect below would force Folders again on the next load of that
    //    URL — overriding both this pick and the stored preference.
    const next = new URLSearchParams(searchParams);
    next.delete("storage");
    if (newMode === "folders") next.delete("view");
    else next.delete("folderId");
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  };

  // A building with one project never asks for a pick (see the toggle's entry
  // path): Folders in Building view enters it directly.
  const soleFolderProjectId =
    viewMode === "folders" &&
    sidebarMode === "building" &&
    selectedProjectId == null &&
    optionsLoaded &&
    projectOptions.length === 1
      ? projectOptions[0].id
      : null;
  useEffect(() => {
    if (soleFolderProjectId != null) setProject(soleFolderProjectId);
  }, [soleFolderProjectId, setProject]);

  // Clears this project's Documents dot on the All buildings page.
  useMarkProjectActivitySeen(
    selectedProjectId,
    "documents",
    viewMode === "folders"
      ? !documentStore.isLoadingProjectDocs && !documentStore.error
      : viewMode === "timeline"
        ? !documentStore.timelineLoading && !documentStore.timelineError
        : !documentStore.isLoading && !documentStore.error
  );

  const legacyProjectId = searchParams.get("projectId");
  // Stable signature of the loaded option ids, so the migration effect below
  // reacts to an option load without re-running on every render (the hook
  // hands back a fresh array each time).
  const projectOptionIds = projectOptions.map((p) => p.id).join(",");

  // An id useBuildingScope has already rejected must not be migrated: its
  // reconcile effect strips the whole rejected scope (`project` + `projectId` +
  // `folderId`) from this same render's snapshot, one effect earlier in the
  // same commit, so writing it back would resurrect what it just deleted and
  // leave the URL flickering through a rejected scope. An unparseable id isn't
  // in the options either, but the hook neither resolves nor strips it — that
  // one is dropped by the migration itself rather than promoted, so the legacy
  // param still gets cleaned up.
  const legacyProjectNum = legacyProjectId ? Number.parseInt(legacyProjectId, 10) : NaN;
  const isRejectedLegacyId =
    optionsLoaded &&
    Number.isFinite(legacyProjectNum) &&
    legacyProjectNum > 0 &&
    !projectOptionIds.split(",").includes(String(legacyProjectNum));

  // Legacy `?projectId=&folderId=` deep links (breadcrumb tooltips, old
  // bookmarks): open the Folders tab (without persisting it as the user's
  // preference) and migrate only the project id to the global `?project=`
  // scope param. `folderId` stays in the URL as the folder view's navigation
  // state — so Back/Forward can traverse folder locations — unless a newer
  // `?project=` wins the scope, in which case the folder belongs to the losing
  // project and goes with it (see migrateLegacyScopeParams).
  useEffect(() => {
    const next = migrateLegacyScopeParams(searchParams, isRejectedLegacyId);
    if (!next) return;
    setRawViewMode("folders");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, isRejectedLegacyId]);

  // A `?folderId=` on the URL is a folder location — the canonical shape
  // handleFolderSelect writes and the one a user copies out of the address bar.
  // Only the Folders tab can show it, so opening such a link forces that tab
  // whatever the stored preference says; otherwise the link silently drops the
  // folder it names and lands on Timeline/List. Not written to storage, so the
  // user's own preference survives the visit. Keyed on the param's presence,
  // not its value: switching tabs by hand with a folder still on the URL must
  // not snap back to Folders, and stepping between folders must not re-force a
  // tab the user has since left.
  const hasFolderParam = searchParams.has("folderId");
  useEffect(() => {
    if (hasFolderParam) setRawViewMode("folders");
  }, [hasFolderParam]);

  const handleViewTrash = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("view", "trash");
    setSearchParams(newSearchParams);
  };

  const { detail, isLoading, error } = useBuildingDetail(id);
  const buildingName = detail?.base?.name;

  // Scope the document store to this building AND the header's project for
  // EVERY tab. List/Timeline get the building from loadDocuments, but the
  // Folders tab never calls that, and neither tab reports the project scope —
  // while a bulk action now recovers ids no surface renders via an id-only
  // server lookup, so a selection left behind in the scope the user just left
  // would resolve (same org, so RLS returns it) and be trashed, zipped or
  // signed from this one.
  useEffect(() => {
    if (buildingId) documentStore.setDocumentScope(buildingId, selectedProjectIdStr);
  }, [buildingId, selectedProjectIdStr, documentStore]);

  // The filter values are page-level, so every tab's popover offers the same
  // building- (or project-) scoped set. It lives HERE rather than in
  // DocumentsToolsRow because that row stands down in the trash views — and the
  // Folders tab keeps its rows behind a spinner until these values resolve, so
  // a scope whose only loader was unmounted left the tab loading for ever.
  // Gated on org readiness like every other org-scoped fetch.
  useEffect(() => {
    if (!buildingId || !isReady || !currentOrganization || !userProfile) return;
    documentStore.loadAvailableFilters(buildingId, selectedProjectIdStr ?? undefined).catch(() => {
      // Already logged by the store; availableFilters stays null until the
      // popover-open retry (or a dep change) retriggers the load.
    });
  }, [buildingId, selectedProjectIdStr, documentStore, isReady, currentOrganization, userProfile]);

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>{t.common.error}</AlertTitle>
          <AlertDescription>{error || t.common.loadingError}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const tabOptions: Array<UnderlineTabOption<DocViewMode>> = availableModes.map((mode) => {
    switch (mode) {
      case "folders":
        return { value: mode, label: t.documents.viewToggle.folders, icon: <FolderOpen /> };
      case "timeline":
        return { value: mode, label: t.documents.viewToggle.timeline, icon: <Clock /> };
      case "list":
        return { value: mode, label: t.documents.viewToggle.list, icon: <List /> };
    }
  });

  // Both trash surfaces carry their own toolbar, so the page tools stand down.
  const showPageTools = !isTrashView && !(viewMode === "folders" && isFolderTrashOpen);

  const scopedProjectName =
    selectedProjectId != null
      ? projectOptions.find((p) => p.id === selectedProjectId)?.name
      : undefined;

  return (
    <div>
      <DocumentCommentsPanelProvider
        buildingId={Number.isFinite(buildingIdNum) && buildingIdNum > 0 ? buildingIdNum : null}
        realtime
      >
        <CommentsDeepLink />
        <DocumentFiltersProvider>
          <StorageLargestFilesSort />
          <PageHeaderActionsProvider target={headerActionsNode}>
            <>
              {/* Header: row 1 is the title + subtitle with the active tab's
                Upload button and overflow menu right-aligned; row 2 is the view
                tab strip with the page-owned filters, search and history tools
                right-aligned on the same row (they wrap beneath the tabs when
                the row runs out of width). The block's own bottom border runs
                edge to edge (padding is inside it), so the rule under the tabs
                spans the full page width. */}
              <div className="border-b px-6 pt-6">
                <div className="mb-3 flex items-start justify-between gap-4">
                  <div>
                    <PageTitle info="documents">{t.buildings.sidebar.documents}</PageTitle>
                    <PageSubtitle>{t.documents.manage.subtitle}</PageSubtitle>
                  </div>
                  {/* sm:min-w-[28rem] reserves the slot's width so the tools row
                      below keeps its position across tab switches — but only once
                      the title column can spare it. Unconditional, a header
                      holding just the Upload button (or nothing, in trash) would
                      overflow horizontally below ~600px. */}
                  <div className="flex flex-shrink-0 items-center justify-end gap-2 sm:min-w-[28rem]">
                    {isTrashView && viewMode !== "folders" && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="controlIcon">
                            <MoreVertical />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setIsManageSharesDialogOpen(true)}>
                            <LinkIcon className="mr-2 h-4 w-4" />
                            {t.documents.manage.manageShares}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    {/* Filled by the active tab via <PageHeaderActions>. */}
                    <div ref={setHeaderActionsNode} className="flex items-center gap-2" />
                  </div>
                </div>

                <StorageCriticalBanner buildingId={buildingId} orgId={currentOrganization?.id} />

                {/* The tools belong to the page, not the mounted tab, so they
                  survive a tab switch; trash views hide them because they carry
                  their own toolbar. Tabs and tools share one row to keep the
                  header compact: items-end keeps the strip's -mb-px underline
                  on the header's bottom border, pb-2 lifts the tools off that
                  border, and ml-auto keeps them right-aligned even after they
                  wrap onto their own line at narrow widths. */}
                {(tabOptions.length > 1 || showPageTools) && (
                  <div className="flex flex-wrap items-end gap-x-4">
                    {tabOptions.length > 1 && (
                      // -mb-px so the strip's last pixel row lands on the header's
                      // own bottom border rather than stacking a second line under
                      // it (the strip draws no rule of its own).
                      <UnderlineTabs
                        className="-mb-px min-w-0"
                        value={viewMode}
                        onValueChange={handleViewModeChange}
                        options={tabOptions}
                        infoTarget="documents.views"
                      />
                    )}
                    {showPageTools && (
                      // Grows into the space the tabs leave, which is where the
                      // active filter chips sit (left of the Filters button).
                      <div className="ml-auto min-w-0 flex-[1_1_auto] pb-2">
                        <DocumentsToolsRow
                          buildingId={buildingId}
                          selectedProjectId={selectedProjectIdStr}
                          canViewHistory={canViewDocumentHistory}
                          onOpenHistory={() => setIsDocumentHistoryOpen(true)}
                          historyTriggerRef={historyTriggerRef}
                          searchPlaceholder={
                            viewMode === "folders" ? t.documents.table.searchInFolder : undefined
                          }
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* pt-2: the view's own toolbar row sits close under the tab rule
                — a taller pad stranded it in whitespace. */}
              <div className="px-6 pb-6 pt-2">
                {viewMode === "folders" ? (
                  selectedProjectId == null && sidebarMode === "building" ? (
                    <BuildingDocumentFolders
                      projects={projectOptions}
                      onOpen={setProject}
                      loading={!optionsLoaded && !optionsError}
                      error={optionsError}
                      onRetry={reloadOptions}
                    />
                  ) : selectedProjectId == null ? (
                    // Transient only: the layout's project gate guarantees a
                    // concrete project in Projects view once the options load.
                    <div className="flex min-h-[200px] items-center justify-center">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <ProjectScopedFolderView
                      buildingId={buildingId}
                      projectId={String(selectedProjectId)}
                      projectName={scopedProjectName}
                      // Enables the tab's New document action. Passed down rather
                      // than re-fetched inside the view: this page already gates
                      // its render on the building detail, so a second fetch
                      // there would unmount the view and loop.
                      buildingName={buildingName || `Building ${id}`}
                      onManageShares={() => setIsManageSharesDialogOpen(true)}
                      hideInlineSearch
                      // The sidebar heading already names the project, so the
                      // folder view drops its own project heading — its folder
                      // breadcrumb still renders once a folder is selected.
                      hideProjectName
                      onTrashViewChange={setIsFolderTrashOpen}
                    />
                  )
                ) : (
                  <BuildingDocumentsComponent
                    buildingId={buildingId}
                    buildingName={buildingName || `Building ${id}`}
                    selectedProjectId={selectedProjectIdStr}
                    onViewTrash={handleViewTrash}
                    viewMode={viewMode}
                    onManageShares={() => setIsManageSharesDialogOpen(true)}
                  />
                )}
              </div>
            </>
          </PageHeaderActionsProvider>
        </DocumentFiltersProvider>
      </DocumentCommentsPanelProvider>

      {canViewDocumentHistory && (
        <DocumentHistoryDialog
          open={isDocumentHistoryOpen}
          onOpenChange={setIsDocumentHistoryOpen}
          buildingId={buildingId}
          buildingName={buildingName || `Building ${id}`}
          orgId={currentOrganization?.id}
          projectId={selectedProjectId}
          projectName={scopedProjectName ?? null}
          returnFocusRef={historyTriggerRef}
        />
      )}

      {/* Manage Shares Dialog */}
      <ManageSharesDialog
        open={isManageSharesDialogOpen}
        onOpenChange={setIsManageSharesDialogOpen}
        buildingId={buildingId}
      />
    </div>
  );
});

export default BuildingDocuments;
